import React, { useState, useEffect } from 'react';
import { useData } from '../DataContext';
import { useLang } from '../LanguageContext'; // ★ 언어 훅
import { useLocation } from 'react-router-dom';
import { 
  Plus, Search, X, Upload, Save, UserCheck, Edit, FileText, Camera, Scan, ArrowRightLeft, Send,
  ChevronLeft, ChevronRight // ★ 페이지네이션 아이콘 추가
} from 'lucide-react';

const COUNTRY_LIST = [
  { code: 'ph', name: 'Philippines', dial: '+63' },
  { code: 'kr', name: 'South Korea', dial: '+82' },
  { code: 'cn', name: 'China', dial: '+86' },
  { code: 'jp', name: 'Japan', dial: '+81' },
  { code: 'us', name: 'USA', dial: '+1' },
  { code: 'vn', name: 'Vietnam', dial: '+84' },
  { code: 'th', name: 'Thailand', dial: '+66' },
  { code: 'sg', name: 'Singapore', dial: '+65' },
  { code: 'my', name: 'Malaysia', dial: '+60' },
  { code: 'hk', name: 'Hong Kong', dial: '+852' },
  { code: 'mo', name: 'Macau', dial: '+853' },
  { code: 'tw', name: 'Taiwan', dial: '+886' },
  { code: 'au', name: 'Australia', dial: '+61' },
  { code: 'gb', name: 'UK', dial: '+44' }
];

const Agents = () => {
  const { agents = [], addAgent, updateAgent, agentTransactions = [], processAgentTransaction, currentUser } = useData(); 
  const { t } = useLang(); 
  const location = useLocation(); 
  
  // ★ [권한 체크] 'Manager' 단어가 포함된 직급(Senior Manager 등)은 무조건 관리자로 인식
  const userType = currentUser?.type || '';
  const isAdmin = 
      userType === 'Master_Admin' || 
      userType === 'Staff' || 
      userType.includes('Manager');

  const isWritable = isAdmin;

  // --- Pagination State (페이지네이션) ---
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20; // ★ 한 페이지당 20개 표시

  // --- Modals State ---
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);
  const [transferModal, setTransferModal] = useState({ isOpen: false });
  const [selectedAgent, setSelectedAgent] = useState(null);
  
  // --- Search & Filter State ---
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('All');
  
  // --- Form & Validation State ---
  const [isIdChecked, setIsIdChecked] = useState(false);
  const [showUplineSuggestions, setShowUplineSuggestions] = useState(false);
  const [showIdSuggestions, setShowIdSuggestions] = useState(false);

  // --- Transaction State ---
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, data: null });
  const [selectedCountry, setSelectedCountry] = useState(COUNTRY_LIST[0]); 
  
  const [fundAmount, setFundAmount] = useState('');
  const [fundRemark, setFundRemark] = useState('');
  const [fundConfirm, setFundConfirm] = useState({ isOpen: false, type: '' });

  // --- Transfer State ---
  const [transferTargetId, setTransferTargetId] = useState('');
  const [transferAmount, setTransferAmount] = useState('');
  const [transferRemark, setTransferRemark] = useState('');
  const [showTransferTargetSuggestions, setShowTransferTargetSuggestions] = useState(false);

  // 타 페이지에서 Agent ID를 클릭해서 넘어왔을 때 자동 오픈
  useEffect(() => {
    if (location.state?.targetAgentCode && agents.length > 0) {
        const targetCode = location.state.targetAgentCode;
        const targetAgent = agents.find(a => a.code === targetCode);
        
        if (targetAgent) {
            setSelectedAgent(targetAgent);
            setIsAccountModalOpen(true);
            setFundAmount('');
            setFundRemark('');
            window.history.replaceState({}, document.title);
        }
    }
  }, [location.state, agents]);

  // 검색어나 필터 변경 시 페이지를 1로 초기화
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterType]);

  const fmt = (num) => num ? Math.floor(Number(num)).toLocaleString() : '0';

  const handleFundAmountChange = (e) => {
    const val = e.target.value.replace(/[^0-9]/g, '');
    setFundAmount(val ? Number(val).toLocaleString() : '');
  };

  const handleTransferAmountChange = (e) => {
    const val = e.target.value.replace(/[^0-9]/g, '');
    setTransferAmount(val ? Number(val).toLocaleString() : '');
  };

  const handleTransferTargetIdChange = (e) => {
    setTransferTargetId(e.target.value.toUpperCase());
    setShowTransferTargetSuggestions(true);
  };

  const handleSelectTransferTargetSuggestion = (code) => {
    setTransferTargetId(code);
    setShowTransferTargetSuggestions(false);
  };

  // ★ [필터링 로직 수정] Senior Manager 포함 관리자는 무조건 통과
  const filteredAgents = (agents || [])
    .filter(agent => agent.code !== 'MASTER_ADMIN')
    .filter(agent => {
        // 1. 관리자(isAdmin)는 모든 에이전트 표시 (조건 없음)
        if (isAdmin) return true;

        // 2. 일반 에이전트는 본인과 본인의 하위 에이전트만 표시
        if (agent.code === currentUser.code || agent.upline === currentUser.code) {
            return true;
        }
        return false;
    })
    .filter(agent => {
        if (filterType !== 'All' && agent.type !== filterType) return false;
        
        const query = searchTerm.toLowerCase().trim();
        const code = (agent.code || '').toLowerCase();
        const engName = ((agent.engSurname || '') + ' ' + (agent.engGivenName || '')).toLowerCase();
        const nativeName = (agent.nativeName || '').toLowerCase();
        
        return code.includes(query) || engName.includes(query) || nativeName.includes(query);
    });

  // ★ [페이지네이션 계산 로직]
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentAgents = filteredAgents.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredAgents.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
        setCurrentPage(pageNumber);
    }
  };

  const initialFormState = { 
      type: 'Agent', 
      accountId: '', 
      password: '', 
      engSurname: '', 
      engGivenName: '', 
      nativeName: '', 
      email: '', 
      nationality: 'Philippines', 
      gender: 'Male', 
      upline: '', 
      membershipCard: '', 
      phone: '', 
      telegramId: '', 
      remark: '', 
      passportImage: null, 
      rollingRate: '', 
      shareCap: '' 
  };
  const [formData, setFormData] = useState(initialFormState);

  const handleChange = (e) => { 
    const { name, value } = e.target; 
    let finalValue = value;

    if (['accountId', 'engSurname', 'engGivenName', 'nativeName', 'upline'].includes(name)) {
        finalValue = value.toUpperCase();
    }

    setFormData(prev => ({ ...prev, [name]: finalValue })); 
    
    if (name === 'accountId') {
        setIsIdChecked(false);
        setShowIdSuggestions(true);
    }
    if (name === 'upline') setShowUplineSuggestions(true);
  };
  
  const handleNationalityChange = (e) => setFormData(prev => ({ ...prev, nationality: e.target.value }));
  const handlePhoneCodeChange = (e) => {
      const country = COUNTRY_LIST.find(c => c.dial === e.target.value);
      if (country) setSelectedCountry(country);
  };
  
  const handleFileChange = (e) => { 
      const file = e.target.files[0]; 
      if (file) {
          const reader = new FileReader();
          reader.onloadend = () => {
              setFormData(prev => ({ ...prev, passportImage: reader.result }));
          };
          reader.readAsDataURL(file);
      }
  };
  
  const handleCheckDuplicate = () => { 
      if (!formData.accountId) return alert("Please enter ID.");
      const exists = agents.some(agent => agent.code === formData.accountId);
      if (exists) {
          alert("ID already exists.");
          setIsIdChecked(false);
      } else {
          alert("ID is available.");
          setIsIdChecked(true);
      }
  };
  
  const handleCreateClick = () => { 
      setIsEditMode(false); 
      setFormData(initialFormState); 
      setIsIdChecked(false); 
      setShowIdSuggestions(false);
      setIsModalOpen(true); 
  };
  
  const handleEditClick = (agent) => { 
      setIsEditMode(true); 
      setEditingId(agent.docId || agent.id); 
      
      setFormData({ 
          ...initialFormState, 
          ...agent, 
          accountId: agent.code,
          telegramId: agent.telegramId || '', 
          passportImage: agent.passportImage || null,
          rollingRate: agent.rollingRate || '',
          shareCap: agent.shareCap || ''
      }); 
      
      const foundCountry = COUNTRY_LIST.find(c => agent.phone && agent.phone.startsWith(c.dial));
      if (foundCountry) setSelectedCountry(foundCountry);

      setIsIdChecked(true); 
      setShowIdSuggestions(false);
      setIsModalOpen(true); 
  };
  
  const handlePreSubmit = () => { 
      if (!isEditMode && !isIdChecked) return alert("Please check ID duplication.");
      if (!formData.accountId) return alert("Account ID is required.");
      if (!formData.password) return alert("Password is required.");
      if (!formData.engSurname) return alert("Surname is required.");
      if (!formData.engGivenName) return alert("Given Name is required.");
      if (!formData.nationality) return alert("Nationality is required.");
      if (!formData.phone) return alert("Mobile Phone is required.");

      setConfirmModal({ isOpen: true, data: formData }); 
  };

  const handleFinalSubmit = async () => {
    let finalPhone = formData.phone;
    if (!finalPhone.startsWith(selectedCountry.dial)) {
        finalPhone = `${selectedCountry.dial} ${formData.phone.trim()}`;
    }

    const finalData = {
        ...formData,
        code: formData.accountId, 
        phone: finalPhone
    };

    try {
        if (isEditMode) {
            await updateAgent({ ...finalData, docId: editingId }, 'Update Info');
            alert("Agent updated successfully.");
        } else {
            await addAgent(finalData);
            alert("New Agent created successfully.");
        }
        setConfirmModal({ isOpen: false }); 
        setIsModalOpen(false);

    } catch (error) {
        console.error("Agent Save Error:", error);
        alert("Error occurred: " + error.message);
    }
  };

  const uplineSuggestions = (agents || []).filter(agent => 
    formData.upline && (agent.code || '').includes(formData.upline) && agent.code !== formData.accountId && (agent.type === 'Group' || agent.type === 'Master_Admin')
  );

  const idSuggestions = (agents || []).filter(agent => 
    formData.accountId && !isEditMode && 
    (agent.code || '').includes(formData.accountId)
  );

  const transferTargetSuggestions = (agents || []).filter(agent => 
    transferTargetId &&
    ((agent.code || '').includes(transferTargetId) || 
     (agent.engSurname || '').toUpperCase().includes(transferTargetId) || 
     (agent.engGivenName || '').toUpperCase().includes(transferTargetId) ||
     (agent.nativeName || '').toUpperCase().includes(transferTargetId)) &&
    agent.code !== selectedAgent?.code
  );
  
  const handleSelectUpline = (code) => { setFormData(prev => ({ ...prev, upline: code })); setShowUplineSuggestions(false); };
  
  const handleSelectIdSuggestion = (code) => {
    setFormData(prev => ({ ...prev, accountId: code }));
    setShowIdSuggestions(false);
    setIsIdChecked(false); 
  };

  const handleAccountClick = (agent) => { setSelectedAgent(agent); setIsAccountModalOpen(true); setFundAmount(''); setFundRemark(''); };

  const triggerFundConfirm = (type) => {
      if (!fundAmount) return alert("Please enter amount.");
      setFundConfirm({ isOpen: true, type });
  };

  const executeFundTransaction = async () => {
    const amount = parseFloat(fundAmount.replace(/,/g, ''));
    if(!processAgentTransaction) return alert("System Error: Check DataContext");
    const success = await processAgentTransaction(
        selectedAgent.docId || selectedAgent.id,
        selectedAgent.code, 
        fundConfirm.type, 
        amount, 
        fundRemark
    );
    if (success) {
        setFundConfirm({ isOpen: false, type: '' });
        setFundAmount(''); setFundRemark('');
    } else {
        alert("Transaction failed (Insufficient funds etc.)");
    }
  };

  const executeTransfer = async () => {
    if (!transferTargetId) return alert("Please enter recipient ID.");
    if (!transferAmount) return alert("Please enter amount.");
    
    const targetAgent = agents.find(a => a.code === transferTargetId.toUpperCase());
    if (!targetAgent) return alert("Invalid Account ID.");
    if (targetAgent.code === selectedAgent.code) return alert("Cannot transfer to self.");

    const amount = parseFloat(transferAmount.replace(/,/g, ''));
    if (amount > (selectedAgent.balance || 0)) return alert("Insufficient balance.");

    if(!processAgentTransaction) return alert("System Error: Check DataContext");

    // Sender
    const senderNewBalance = (selectedAgent.balance || 0) - amount;
    await updateAgent({ ...selectedAgent, balance: senderNewBalance }, `Transfer Out: ${amount}`);

    const senderRemark = transferRemark 
        ? `${transferRemark} (To: ${targetAgent.code})` 
        : `Transfer to ${targetAgent.code}`;
    
    await processAgentTransaction(
        selectedAgent.docId || selectedAgent.id,
        selectedAgent.code, 
        'Transfer', 
        amount, 
        senderRemark,
        null 
    );

    // Receiver
    const receiverNewBalance = (targetAgent.balance || 0) + amount;
    await updateAgent({ ...targetAgent, balance: receiverNewBalance }, `Transfer In: ${amount}`);

    const receiverRemark = transferRemark 
        ? `${transferRemark} (From: ${selectedAgent.code})` 
        : `Transfer from ${selectedAgent.code}`;

    await processAgentTransaction(
        targetAgent.docId || targetAgent.id,
        targetAgent.code, 
        'Transfer In',  
        amount, 
        receiverRemark,
        null
    );

    setTransferModal({ isOpen: false });
    setTransferTargetId(''); setTransferAmount(''); setTransferRemark('');
    alert("Transfer completed.");
  };

  const myTransactions = (agentTransactions || []).filter(t => selectedAgent && t.agentCode === selectedAgent.code);

  return (
    <div style={{ padding: '30px', color: '#1e293b' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'20px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0, color: '#0f172a' }}>{t('agent_title')}</h2>
            <div style={{ display: 'flex', gap: '8px' }}>
                {['All', 'Group', 'Agent'].map(type => (
                    <button 
                        key={type} 
                        onClick={() => setFilterType(type)}
                        style={{
                            padding: '6px 14px', borderRadius: '20px', border: 'none', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer',
                            backgroundColor: filterType === type ? '#0f172a' : '#e2e8f0',
                            color: filterType === type ? 'white' : '#64748b',
                            transition: 'all 0.2s'
                        }}
                    >
                        {type === 'All' ? t('filter_all') : (type === 'Group' ? t('filter_group') : t('filter_agent'))}
                    </button>
                ))}
            </div>
            <div style={{ position: 'relative', marginLeft: '10px' }}>
                <input 
                    value={searchTerm} 
                    onChange={(e) => setSearchTerm(e.target.value)} 
                    placeholder={t('search_place_agent')} 
                    style={{ padding: '8px 8px 8px 35px', borderRadius: '20px', border: '1px solid #cbd5e1', width: '250px', outline: 'none', fontSize: '14px' }} 
                />
                <Search size={18} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            </div>
        </div>
        <div style={{ display:'flex', gap:'10px' }}>
          <button onClick={() => window.print()} style={{ backgroundColor: '#dc2626', color: 'white', padding: '12px 20px', border: 'none', borderRadius: '8px', fontWeight: 'bold', display:'flex', alignItems:'center', gap:'5px', cursor:'pointer' }}><FileText size={18}/> {t('pdf')}</button>
          
          {isWritable && (
            <button onClick={handleCreateClick} style={{ backgroundColor: '#2563eb', color: 'white', padding: '12px 20px', border: 'none', borderRadius: '8px', fontWeight: 'bold', display:'flex', alignItems:'center', gap:'5px', cursor:'pointer' }}><Plus size={18}/> {t('agent_new')}</button>
          )}
        </div>
      </div>

      <div id="main-table" style={{ overflowX: 'auto', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              <th style={{ padding: '15px', textAlign: 'center' }}>{t('th_id')}</th>
              <th style={{ padding: '15px', textAlign: 'center' }}>{t('th_name')}</th>
              <th style={{ padding: '15px', textAlign: 'center' }}>{t('type')}</th>
              <th style={{ padding: '15px', textAlign: 'center' }}>{t('th_phone')}</th>
              <th style={{ padding: '15px', textAlign: 'center' }}>{t('status')}</th>
              <th className="no-print" style={{ padding: '15px', textAlign: 'center' }}>{t('action')}</th>
              <th className="no-print" style={{ padding: '15px', textAlign: 'center' }}>{t('agent_account')}</th>
              <th style={{ padding: '15px', textAlign: 'center' }}>{t('th_creator')}</th>
            </tr>
          </thead>
          <tbody>
            {/* ★ [수정] currentAgents (페이지네이션된 데이터) 사용 */}
            {currentAgents.length > 0 ? (
                currentAgents.map((agent) => (
                  <tr key={agent.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '15px', textAlign: 'center', fontWeight: 'bold' }}>{agent.code}</td>
                    <td style={{ padding: '15px', textAlign: 'center' }}>
                        {agent.engSurname} {agent.engGivenName} 
                        {agent.nativeName && <span style={{color:'#64748b', fontSize:'12px', marginLeft:'5px'}}>({agent.nativeName})</span>}
                    </td>
                    <td style={{ padding: '15px', textAlign: 'center' }}>
                        <span style={{ 
                            padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold',
                            backgroundColor: agent.type === 'Group' ? '#fff7ed' : '#eff6ff', 
                            color: agent.type === 'Group' ? '#c2410c' : '#1e40af',
                            border: agent.type === 'Group' ? '1px solid #fdba74' : '1px solid #93c5fd'
                        }}>
                            {agent.type}
                        </span>
                    </td>
                    <td style={{ padding: '15px', textAlign: 'center' }}>
                        <div>{agent.phone}</div>
                        {agent.telegramId && <div style={{ fontSize:'12px', color:'#2563eb', display:'flex', alignItems:'center', justifyContent:'center', gap:'3px' }}><Send size={12}/> {agent.telegramId}</div>}
                    </td>
                    <td style={{ padding: '15px', textAlign: 'center', color:'#16a34a', fontWeight:'bold' }}>Active</td>
                    <td className="no-print" style={{ padding: '15px', textAlign: 'center' }}>
                        {isWritable && (
                            <button onClick={() => handleEditClick(agent)} style={{ padding:'6px 12px', border:'1px solid #cbd5e1', borderRadius:'6px', background:'white', cursor:'pointer' }}>{t('edit')}</button>
                        )}
                    </td>
                    <td className="no-print" style={{ padding: '15px', textAlign: 'center' }}>
                        <button onClick={() => handleAccountClick(agent)} style={{ padding:'6px 12px', background:'#0f172a', color:'white', border:'none', borderRadius:'4px', cursor:'pointer' }}>{t('view')}</button>
                    </td>
                    <td style={{ padding: '15px', textAlign: 'center', color:'#64748b' }}>{agent.creator}</td>
                  </tr>
                ))
            ) : (
                <tr>
                    <td colSpan="8" style={{ padding: '30px', textAlign: 'center', color: '#94a3b8' }}>
                        {isAdmin ? "데이터가 없습니다. (No Agents Found)" : "권한이 없거나 데이터가 없습니다."}
                    </td>
                </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ★ [추가] 페이지네이션 컨트롤 UI */}
      {totalPages > 1 && (
        <div className="no-print" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '20px' }}>
            <button 
                onClick={() => handlePageChange(currentPage - 1)} 
                disabled={currentPage === 1}
                style={{ padding: '8px', borderRadius: '50%', border: '1px solid #cbd5e1', background: 'white', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', opacity: currentPage === 1 ? 0.5 : 1 }}
            >
                <ChevronLeft size={20} />
            </button>
            <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#334155' }}>
                Page {currentPage} of {totalPages}
            </span>
            <button 
                onClick={() => handlePageChange(currentPage + 1)} 
                disabled={currentPage === totalPages}
                style={{ padding: '8px', borderRadius: '50%', border: '1px solid #cbd5e1', background: 'white', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', opacity: currentPage === totalPages ? 0.5 : 1 }}
            >
                <ChevronRight size={20} />
            </button>
        </div>
      )}
      
      {/* ... Modals (Account Sheet) ... */}
      {isAccountModalOpen && selectedAgent && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1100 }}>
            <div id="account-sheet" style={{ backgroundColor: 'white', width: '900px', height: '85vh', borderRadius: '12px', padding: '30px', overflowY: 'auto', display:'flex', flexDirection:'column' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px' }}>
                    <h2 style={{ margin:0, color:'#0f172a' }}>💳 {selectedAgent.code} - {t('agent_account')}</h2>
                    <div style={{ display:'flex', gap:'10px' }}>
                        <button onClick={() => window.print()} className="no-print" style={{ padding:'8px 12px', background:'#dc2626', color:'white', border:'none', borderRadius:'6px', cursor:'pointer' }}>{t('pdf')}</button>
                        <button onClick={() => setIsAccountModalOpen(false)} className="no-print" style={{ border:'none', background:'none', cursor:'pointer' }}><X size={24}/></button>
                    </div>
                </div>
                
                <div className="no-print" style={{ padding:'20px', backgroundColor:'#f1f5f9', borderRadius:'8px', marginBottom:'20px' }}>
                    <div style={{ display:'flex', gap:'10px', alignItems:'flex-end' }}>
                        <div><label style={{ fontSize:'12px', fontWeight:'bold', display:'block' }}>{t('agent_balance')}</label><div style={{ fontSize:'24px', fontWeight:'bold', color:'#2563eb' }}>{fmt(selectedAgent.balance || 0)}</div></div>
                        <div style={{ flex:1 }}></div>
                        <div><label style={{ fontSize:'12px', display:'block' }}>{t('amount')}</label><input type="text" value={fundAmount} onChange={handleFundAmountChange} style={{ display:'block', padding:'10px', borderRadius:'6px', border:'1px solid #ccc' }} placeholder="0" /></div>
                        <div style={{ width:'200px' }}><label style={{ fontSize:'12px', display:'block' }}>{t('remark')}</label><input type="text" value={fundRemark} onChange={e=>setFundRemark(e.target.value)} style={{ width:'100%', padding:'10px', borderRadius:'6px', border:'1px solid #ccc' }} placeholder="Memo" /></div>
                        
                        <div style={{ display:'flex', flexDirection:'column', gap:'5px' }}>
                            <div style={{ display:'flex', gap:'5px' }}>
                                <button onClick={() => triggerFundConfirm('Deposit')} style={{ padding:'10px 20px', background:'#16a34a', color:'white', border:'none', borderRadius:'6px', fontWeight:'bold', cursor:'pointer' }}>{t('agent_deposit')}</button>
                                <button onClick={() => triggerFundConfirm('Withdraw')} style={{ padding:'10px 20px', background:'#dc2626', color:'white', border:'none', borderRadius:'6px', fontWeight:'bold', cursor:'pointer' }}>{t('agent_withdraw')}</button>
                            </div>
                            <button 
                                onClick={() => {
                                  setTransferModal({ isOpen: true });
                                  setShowTransferTargetSuggestions(false);
                                }}
                                style={{ 
                                    display:'flex', alignItems:'center', justifyContent:'center', gap:'6px',
                                    padding:'10px', background:'#4f46e5', color:'white',
                                    border:'none', borderRadius:'6px', fontWeight:'bold', cursor:'pointer',
                                    width: '100%' 
                                }}
                            >
                                <ArrowRightLeft size={16} /> Transfer
                            </button>
                        </div>
                    </div>
                </div>

                <div style={{ flex:1, overflowY:'auto', border:'1px solid #e2e8f0', borderRadius:'8px' }}>
                    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'13px' }}>
                        <thead style={{ position:'sticky', top:0, background:'#f8fafc' }}><tr style={{ borderBottom:'1px solid #e2e8f0' }}><th style={{ padding:'12px' }}>{t('date')}</th><th style={{ padding:'12px' }}>{t('type')}</th><th style={{ padding:'12px', textAlign:'right' }}>{t('agent_deposit')}</th><th style={{ padding:'12px', textAlign:'right' }}>{t('agent_withdraw')}</th><th style={{ padding:'12px', textAlign:'right' }}>{t('agent_balance')}</th><th style={{ padding:'12px' }}>{t('remark')}</th><th style={{ padding:'12px' }}>{t('staff')}</th></tr></thead>
                        <tbody>{myTransactions.map((tx, idx) => {
                            const isPlus = ['Deposit','Return','Rolling Commission','Share Profit','Game Win', 'Transfer In', 'Rebate'].includes(tx.type);
                            return (
                                <tr key={idx} style={{ borderBottom:'1px solid #f1f5f9' }}>
                                    <td style={{ padding:'12px' }}>{new Date(tx.date).toLocaleString()}</td>
                                    <td style={{ padding:'12px' }}>{tx.type}</td>
                                    <td style={{ padding:'12px', textAlign:'right', color:'#16a34a' }}>{isPlus ? fmt(tx.amount) : '-'}</td>
                                    <td style={{ padding:'12px', textAlign:'right', color:'#dc2626' }}>{!isPlus ? fmt(tx.amount) : '-'}</td>
                                    <td style={{ padding:'12px', textAlign:'right', fontWeight:'bold' }}>{fmt(tx.balance)}</td>
                                    <td style={{ padding:'12px', color:'#64748b' }}>
                                        {tx.remark}
                                        {(tx.gameId || tx.roundId) && tx.type !== 'Transfer' && tx.type !== 'Transfer In' && (
                                            <div style={{ fontSize:'11px', color:'#94a3b8', marginTop:'2px' }}>
                                                Game ID: {tx.gameId || tx.roundId}
                                            </div>
                                        )}
                                    </td>
                                    <td style={{ padding:'12px' }}>{tx.staff}</td>
                                </tr>
                            )
                        })}</tbody>
                    </table>
                </div>
            </div>
        </div>
      )}

      {/* ... (이하 Transfer Modal, Edit Modal, Confirm Modal 등 기존 코드와 동일) ... */}
      {/* ... (생략된 모달 코드들은 원본 코드를 그대로 사용해주세요) ... */}
      {/* --- Transfer Modal --- */}
      {transferModal.isOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1200 }}>
            <div style={{ background:'white', padding:'30px', borderRadius:'12px', width:'400px', boxShadow:'0 10px 25px rgba(0,0,0,0.2)' }}>
                <h3 style={{ marginTop:0, display:'flex', alignItems:'center', gap:'10px', color:'#4f46e5' }}>
                    <ArrowRightLeft /> Transfer Funds
                </h3>
                
                <div style={{ marginTop:'20px' }}>
                    <label style={{ display:'block', fontSize:'13px', fontWeight:'bold', color:'#64748b', marginBottom:'5px' }}>From (Sender)</label>
                    <div style={{ padding:'10px', background:'#f1f5f9', borderRadius:'6px', color:'#334155', fontWeight:'bold' }}>
                        {selectedAgent?.code} (Balance: {fmt(selectedAgent?.balance)})
                    </div>
                </div>

                <div style={{ marginTop:'15px' }}>
                    <label style={{ display:'block', fontSize:'13px', fontWeight:'bold', color:'#64748b', marginBottom:'5px' }}>To (Recipient ID)</label>
                    <input 
                        value={transferTargetId} 
                        onChange={handleTransferTargetIdChange} 
                        placeholder="Ex) TEST002"
                        style={{ width:'100%', padding:'10px', borderRadius:'6px', border:'1px solid #cbd5e1', fontSize:'14px' }} 
                    />
                    {showTransferTargetSuggestions && transferTargetSuggestions.length > 0 && (
                        <ul style={{ position: 'absolute', top: '100%', left:0, width: '100%', background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px', listStyle: 'none', padding: 0, width:'340px', zIndex:10 }}>
                            {transferTargetSuggestions.map((agent) => (
                                <li key={agent.id} onClick={() => handleSelectTransferTargetSuggestion(agent.code)} style={{ padding: '10px', cursor: 'pointer', fontSize:'13px', borderBottom: '1px solid #f1f5f9', color: '#64748b' }}>
                                    <strong style={{ color: '#0f172a' }}>{agent.code}</strong> / {agent.engSurname} {agent.engGivenName}
                                    {agent.nativeName && <span style={{ fontSize:'11px', color:'#94a3b8' }}> ({agent.nativeName})</span>}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div style={{ marginTop:'15px' }}>
                    <label style={{ display:'block', fontSize:'13px', fontWeight:'bold', color:'#64748b', marginBottom:'5px' }}>{t('amount')}</label>
                    <input 
                        value={transferAmount} 
                        onChange={handleTransferAmountChange}
                        placeholder="0"
                        style={{ width:'100%', padding:'10px', borderRadius:'6px', border:'1px solid #cbd5e1', fontSize:'14px', textAlign:'right' }} 
                    />
                </div>

                <div style={{ marginTop:'15px' }}>
                    <label style={{ display:'block', fontSize:'13px', fontWeight:'bold', color:'#64748b', marginBottom:'5px' }}>{t('remark')}</label>
                    <input 
                        value={transferRemark} 
                        onChange={(e) => setTransferRemark(e.target.value)}
                        placeholder="Memo for transfer"
                        style={{ width:'100%', padding:'10px', borderRadius:'6px', border:'1px solid #cbd5e1', fontSize:'14px' }} 
                    />
                </div>

                <div style={{ marginTop:'25px', display:'flex', gap:'10px' }}>
                    <button onClick={() => setTransferModal({ isOpen: false })} style={{ flex:1, padding:'12px', background:'#f1f5f9', border:'1px solid #cbd5e1', borderRadius:'6px', fontWeight:'bold', cursor:'pointer', color:'#64748b' }}>{t('cancel')}</button>
                    <button onClick={executeTransfer} style={{ flex:1, padding:'12px', background:'#4f46e5', color:'white', border:'none', borderRadius:'6px', fontWeight:'bold', cursor:'pointer' }}>{t('confirm')}</button>
                </div>
            </div>
        </div>
      )}

      {/* --- Create/Edit Agent Modal --- */}
      {isModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0, 0, 0, 0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', width: '800px', maxHeight: '90vh', overflowY: 'auto', borderRadius: '12px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}>
            <div style={{ padding: '20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, fontSize: '20px', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '10px' }}><UserCheck size={24} color="#2563eb" /> {isEditMode ? t('edit') : t('agent_new')}</h3>
                <button onClick={() => setIsModalOpen(false)} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer' }}><X size={24} /></button>
            </div>
            
            <div style={{ padding: '30px', color: '#334155' }}>
              <div style={{ marginBottom: '25px' }}>
                  <label style={{ color: '#64748b', fontSize: '14px', display: 'block', marginBottom: '10px' }}>{t('type')}</label>
                  <div style={{ display: 'flex', gap: '20px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', backgroundColor: '#f8fafc', padding: '10px 20px', borderRadius: '8px', border: formData.type === 'Group' ? '1px solid #f59e0b' : '1px solid #e2e8f0' }}>
                          <input type="radio" name="type" value="Group" checked={formData.type === 'Group'} onChange={handleChange} />
                          <span style={{ color: formData.type === 'Group' ? '#ea580c' : '#64748b', fontWeight: 'bold' }}>Group</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', backgroundColor: '#f8fafc', padding: '10px 20px', borderRadius: '8px', border: formData.type === 'Agent' ? '1px solid #3b82f6' : '1px solid #e2e8f0' }}>
                          <input type="radio" name="type" value="Agent" checked={formData.type === 'Agent'} onChange={handleChange} />
                          <span style={{ color: formData.type === 'Agent' ? '#2563eb' : '#64748b', fontWeight: 'bold' }}>Agent</span>
                      </label>
                  </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                  <div>
                      <label style={{ display: 'block', color: '#64748b', marginBottom: '5px' }}>ID <span style={{color:'red'}}>*</span></label>
                      <div style={{ display: 'flex', gap: '8px' }}>
                          <div style={{ position: 'relative', flex: 1 }}>
                              <input 
                                name="accountId" 
                                value={formData.accountId} 
                                onChange={handleChange} 
                                disabled={isEditMode} 
                                placeholder="AA1234" 
                                style={{ width: '100%', padding: '12px', background: isEditMode ? '#f1f5f9' : 'white', border: '1px solid #cbd5e1', borderRadius: '6px' }} 
                              />
                              {showIdSuggestions && idSuggestions.length > 0 && !isEditMode && (
                                  <ul style={{ position: 'absolute', top: '100%', left:0, width: '100%', background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px', listStyle: 'none', padding: 0, marginTop:'5px', zIndex: 10, maxHeight: '150px', overflowY: 'auto', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                                      {idSuggestions.map((agent) => (
                                          <li key={agent.id} onClick={() => handleSelectIdSuggestion(agent.code)} style={{ padding: '10px', cursor: 'pointer', fontSize:'13px', borderBottom: '1px solid #f1f5f9', color: '#64748b' }}>
                                              <strong style={{ color: '#0f172a' }}>{agent.code}</strong> (Existing)
                                          </li>
                                      ))}
                                  </ul>
                              )}
                          </div>
                          
                          {!isEditMode && (
                              <button onClick={handleCheckDuplicate} style={{ whiteSpace:'nowrap', padding: '0 15px', backgroundColor: isIdChecked ? '#22c55e' : '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize:'13px', fontWeight:'bold' }}>
                                  {isIdChecked ? 'OK' : 'Check'}
                              </button>
                          )}
                      </div>
                  </div>
                  <div>
                      <label style={{ display: 'block', color: '#64748b', marginBottom: '5px' }}>Password <span style={{color:'red'}}>*</span></label>
                      <input name="password" value={formData.password} onChange={handleChange} style={{ width: '100%', padding: '12px', background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px' }} />
                  </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                  <div><label style={{ display: 'block', color: '#64748b', marginBottom: '5px' }}>Surname <span style={{color:'red'}}>*</span></label><input name="engSurname" placeholder="HONG" value={formData.engSurname} onChange={handleChange} style={{ width: '100%', padding: '12px', background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px' }} /></div>
                  <div><label style={{ display: 'block', color: '#64748b', marginBottom: '5px' }}>Given Name <span style={{color:'red'}}>*</span></label><input name="engGivenName" placeholder="GILDONG" value={formData.engGivenName} onChange={handleChange} style={{ width: '100%', padding: '12px', background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px' }} /></div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                  <div><label style={{ display: 'block', color: '#64748b', marginBottom: '5px' }}>Native Name</label><input name="nativeName" value={formData.nativeName} onChange={handleChange} style={{ width: '100%', padding: '12px', background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px' }} /></div>
                  <div>
                      <label style={{ display: 'block', color: '#64748b', marginBottom: '5px' }}>Nationality <span style={{color:'red'}}>*</span></label>
                      <div style={{ position:'relative', display:'flex', alignItems:'center' }}>
                          <select name="nationality" value={formData.nationality} onChange={handleNationalityChange} style={{ width: '100%', padding: '12px', background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px' }}>
                              {COUNTRY_LIST.map(c => <option key={c.code} value={c.name}>{c.name}</option>)}
                          </select>
                      </div>
                  </div>
              </div>

              {/* ★ Phone & Telegram Section */}
              <div style={{ backgroundColor: '#f8fafc', padding: '20px', borderRadius: '8px', marginBottom: '20px', border:'1px solid #e2e8f0' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                      <div>
                          <label style={{ display: 'block', color: '#64748b', marginBottom: '5px' }}>Phone <span style={{color:'red'}}>*</span></label>
                          <div style={{ display: 'flex', gap: '5px' }}>
                              <div style={{ position:'relative', minWidth:'100px' }}>
                                  <div style={{ position:'absolute', left:'10px', top:'50%', transform:'translateY(-50%)', pointerEvents:'none' }}>
                                      <img src={`https://flagcdn.com/w20/${selectedCountry.code}.png`} alt="flag" />
                                  </div>
                                  <select value={selectedCountry.dial} onChange={handlePhoneCodeChange} style={{ width:'100%', padding: '12px 12px 12px 35px', background: '#e2e8f0', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px' }}>
                                      {COUNTRY_LIST.map(c => (
                                          <option key={c.code} value={c.dial}>{c.dial}</option>
                                      ))}
                                  </select>
                              </div>
                              <input name="phone" placeholder="9123456789" value={formData.phone} onChange={handleChange} style={{ flex: 1, padding: '12px', background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px' }} />
                          </div>
                      </div>
                      <div>
                          <label style={{ display: 'block', color: '#64748b', marginBottom: '5px' }}>Telegram ID</label>
                          <div style={{ position:'relative' }}>
                              <input name="telegramId" placeholder="Bot Chat ID" value={formData.telegramId} onChange={handleChange} style={{ width: '100%', padding: '12px 12px 12px 35px', background: 'white', border: '1px solid #2563eb', borderRadius: '6px' }} />
                              <Send size={16} style={{ position:'absolute', left:'12px', top:'50%', transform:'translateY(-50%)', color:'#2563eb' }} />
                          </div>
                      </div>
                  </div>
                  <div style={{ marginTop: '15px' }}>
                      <label style={{ display: 'block', color: '#64748b', marginBottom: '5px' }}>Upline</label>
                      <input name="upline" placeholder="Search..." value={formData.upline} onChange={handleChange} style={{ width: '100%', padding: '12px', background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px' }} />
                      {showUplineSuggestions && uplineSuggestions.length > 0 && (
                          <ul style={{ position: 'absolute', background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px', listStyle: 'none', padding: 0, marginTop:'5px', zIndex: 10, maxHeight: '150px', overflowY: 'auto' }}>
                              {uplineSuggestions.map((agent) => (
                                  <li key={agent.id} onClick={() => handleSelectUpline(agent.code)} style={{ padding: '10px', cursor: 'pointer', fontSize:'13px', borderBottom: '1px solid #f1f5f9' }}>
                                      <strong>{agent.code}</strong> / {agent.engSurname} {agent.engGivenName} {agent.nativeName ? `/ ${agent.nativeName}` : ''}
                                  </li>
                              ))}
                          </ul>
                      )}
                  </div>
              </div>

              {/* ★ 게임 설정 섹션 (롤링/쉐어) */}
              <div style={{ backgroundColor: '#f8fafc', padding: '20px', borderRadius: '8px', marginBottom: '20px', border:'1px solid #e2e8f0' }}>
                  <div style={{ marginBottom:'10px', fontSize:'14px', fontWeight:'bold', color:'#334155' }}>Game Settings</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                      <div>
                          <label style={{ display: 'block', color: '#64748b', marginBottom: '5px' }}>Rolling Rate (%)</label>
                          <input 
                              name="rollingRate" 
                              type="number"
                              placeholder="e.g. 1.2" 
                              value={formData.rollingRate} 
                              onChange={handleChange} 
                              style={{ width: '100%', padding: '12px', background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px' }} 
                          />
                      </div>
                      <div>
                          <label style={{ display: 'block', color: '#64748b', marginBottom: '5px' }}>Share Cap (%)</label>
                          <input 
                              name="shareCap" 
                              type="number"
                              placeholder="e.g. 50" 
                              value={formData.shareCap} 
                              onChange={handleChange} 
                              style={{ width: '100%', padding: '12px', background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px' }} 
                          />
                      </div>
                  </div>
              </div>

              <div style={{ marginBottom: '25px' }}>
                  <label style={{ display: 'block', color: '#64748b', marginBottom: '10px' }}>Passport Image <span style={{color:'red'}}>*</span></label>
                  
                  {formData.passportImage ? (
                      <div style={{ textAlign: 'center', marginTop: '10px' }}>
                          <img 
                              src={formData.passportImage} 
                              alt="Passport Preview" 
                              style={{ 
                                  display: 'block',
                                  width: '80%',          
                                  height: 'auto',        
                                  margin: '20px auto',   
                                  borderRadius: '12px',
                                  border: '2px solid #e2e8f0',
                                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                              }} 
                          />
                          <button 
                              onClick={() => document.getElementById('passport-upload-input-edit').click()}
                              style={{ 
                                  display: 'inline-flex', alignItems: 'center', gap: '8px',
                                  padding: '10px 20px', backgroundColor: '#f1f5f9', color: '#334155',
                                  border: '1px solid #cbd5e1', borderRadius: '6px',
                                  fontSize: '14px', fontWeight: 'bold', cursor: 'pointer', marginTop: '10px'
                              }}
                          >
                              <Edit size={16} /> Edit Image
                          </button>
                          <input id="passport-upload-input-edit" type="file" accept="image/*" onChange={handleFileChange} style={{ display: 'none' }} />
                      </div>
                  ) : (
                      <div style={{ display: 'flex', gap: '10px' }}>
                          <label style={{ flex: 1, border: '1px dashed #cbd5e1', borderRadius: '8px', padding: '15px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', backgroundColor: '#f8fafc', color: '#64748b' }}>
                              <Upload size={24} style={{ marginBottom: '5px' }} />
                              <span style={{ fontSize: '12px' }}>Upload</span>
                              <input type="file" accept="image/*" onChange={handleFileChange} style={{ display: 'none' }} />
                          </label>
                          <label style={{ flex: 1, border: '1px dashed #cbd5e1', borderRadius: '8px', padding: '15px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', backgroundColor: '#f8fafc', color: '#64748b' }}>
                              <Camera size={24} style={{ marginBottom: '5px' }} />
                              <span style={{ fontSize: '12px' }}>Photo</span>
                              <input type="file" accept="image/*" capture="environment" onChange={handleFileChange} style={{ display: 'none' }} />
                          </label>
                          <label style={{ flex: 1, border: '1px dashed #cbd5e1', borderRadius: '8px', padding: '15px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', backgroundColor: '#f8fafc', color: '#64748b' }}>
                              <Scan size={24} style={{ marginBottom: '5px' }} />
                              <span style={{ fontSize: '12px' }}>Scan</span>
                              <input type="file" accept="image/*" capture="environment" onChange={handleFileChange} style={{ display: 'none' }} />
                          </label>
                      </div>
                  )}
              </div>

              <div style={{ marginBottom: '20px' }}><label style={{ display: 'block', color: '#64748b', marginBottom: '5px' }}>{t('remark')}</label><textarea name="remark" value={formData.remark} onChange={handleChange} rows="2" style={{ width: '100%', padding: '12px', background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px', resize:'none' }}></textarea></div>
              
              <button onClick={handlePreSubmit} style={{ width: '100%', padding: '15px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}><Save size={20} /> {isEditMode ? t('save') : t('save')}</button>
            </div>
          </div>
        </div>
      )}
      
      {/* Confirm Modal */}
      {confirmModal.isOpen && confirmModal.data && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1100 }}>
          <div style={{ backgroundColor: 'white', width: '400px', borderRadius: '15px', overflow: 'hidden', boxShadow: '0 20px 50px rgba(0,0,0,0.3)' }}>
            <div style={{ backgroundColor: '#2563eb', padding: '15px 20px', color: 'white', fontWeight: 'bold', fontSize: '16px' }}>{t('confirm')}</div>
            <div style={{ padding: '25px' }}>
              <div style={{ marginBottom: '15px', fontSize: '14px', color: '#334155', lineHeight: '1.6' }}>
                <p><strong>ID:</strong> {confirmModal.data.accountId}</p>
                <p><strong>Type:</strong> {confirmModal.data.type}</p>
                <p><strong>Name:</strong> {confirmModal.data.engSurname} {confirmModal.data.engGivenName}</p>
              </div>
              <div style={{ textAlign: 'center', color: '#64748b', fontSize: '13px', marginBottom: '20px' }}>Save this agent?</div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => setConfirmModal({isOpen:false, data:null})} style={{ flex: 1, padding: '12px', border: '1px solid #cbd5e1', background: 'white', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>{t('cancel')}</button>
                <button onClick={handleFinalSubmit} style={{ flex: 1, padding: '12px', border: 'none', background: '#2563eb', color: 'white', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>{t('confirm')}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Fund Confirm Modal */}
      {fundConfirm.isOpen && (
          <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1200 }}>
              <div style={{ background:'white', padding:'30px', borderRadius:'10px', width:'350px', textAlign:'center' }}>
                  <h3 style={{ marginTop:0 }}>{t('confirm')}</h3>
                  <div style={{ margin:'20px 0', fontSize:'15px', color:'#333' }}>
                    <p>{t('type')}: {fundConfirm.type}</p>
                    <p>{t('amount')}: <strong style={{ fontSize:'18px', color:'#2563eb' }}>{fundAmount}</strong></p>
                    <p>{t('remark')}: {fundRemark || '-'}</p>
                  </div>
                  <div style={{ marginTop:'25px', display:'flex', gap:'10px' }}>
                      <button onClick={executeFundTransaction} style={{ flex:1, padding:'12px', background:'#2563eb', color:'white', border:'none', borderRadius:'6px', fontWeight:'bold', cursor:'pointer' }}>{t('confirm')}</button>
                      <button onClick={()=>setFundConfirm({isOpen:false, type:''})} style={{ flex:1, padding:'12px', background:'#ccc', border:'none', borderRadius:'6px', fontWeight:'bold', cursor:'pointer' }}>{t('cancel')}</button>
                  </div>
              </div>
          </div>
      )}

      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 10mm;
          }
          
          body, html {
            width: 100%;
            height: auto !important;
            overflow: visible !important;
            background: white !important;
          }
          
          body * { visibility: hidden; }
          
          #main-table, #main-table * { 
            visibility: visible; 
          }
          #main-table { 
            position: absolute; 
            left: 0; 
            top: 0; 
            width: 100%; 
          }

          #account-sheet, #account-sheet * { 
            visibility: visible; 
          }
          #account-sheet { 
            position: absolute !important; 
            left: 0 !important; 
            top: 0 !important; 
            width: 100% !important; 
            height: auto !important; 
            padding: 0 !important; 
            margin: 0 !important;
            background: white !important; 
            z-index: 9999 !important;
            box-shadow: none !important;
            border: none !important;
            overflow: visible !important;
          }
          
          body:has(#account-sheet) #main-table {
             display: none !important;
          }

          #account-sheet table {
            width: 100% !important;
            font-size: 11px !important;
            border-collapse: collapse !important;
          }
          
          #account-sheet th, #account-sheet td {
            padding: 6px 4px !important;
            border-bottom: 1px solid #ccc !important;
          }

          .no-print, button { display: none !important; }
        }
      `}</style>
    </div>
  );
};

export default Agents;